import Utils from 'common/services/Utils';
import { NumericKind, SelectOption } from './types';

// Task #66347: o que se cola numa célula vem do Excel como texto formatado — "1.269,70 €",
// "31.07.2026", "AGEAS " — e tem de ser reduzido ao que a grelha guarda. Estas funções são a fonte
// única dessa limpeza: são usadas ao colar (passo de validação) e ao sair de uma célula editada
// (editor da coluna), para as duas vias darem exatamente o mesmo.

// Separador de milhares e decimal como no resto da aplicação (ver Utils.formatCurrency e MoneyFormat).
const THOUSANDS_SEPARATOR = ' ';
const DECIMAL_SEPARATOR = ',';

// Quatro casas chegam para o que a aplicação guarda (o custo por km é o campo mais fino); acima
// disso é ruído de vírgula flutuante.
const MAX_DECIMALS = 4;

export const formatNumericText = (value: number, kind: NumericKind) => {
    // Dinheiro leva sempre duas casas; os inteiros (kms, anos de garantia) só as levam se vierem
    // mesmo com decimais — arredondá-los seria mudar o valor sem o utilizador dar por isso. As casas
    // a mais só se cortam quando são zeros: 1269.7 fica 1 269,70 e um 0,075 de custo por km
    // continua 0,075 em vez de encolher para 0,07.
    const minimumDecimals = kind === 'money' ? 2 : 0;
    const [integerPart, allDecimals] = Math.abs(value).toFixed(MAX_DECIMALS).split('.');

    let decimalPart = allDecimals;
    while (decimalPart.length > minimumDecimals && decimalPart.endsWith('0')) {
        decimalPart = decimalPart.slice(0, -1);
    }

    const grouped = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, THOUSANDS_SEPARATOR);
    return `${value < 0 ? '-' : ''}${grouped}${decimalPart ? DECIMAL_SEPARATOR + decimalPart : ''}`;
};

// Grupos de exatamente três algarismos são separadores de milhares, nunca decimais: o parse genérico
// da aplicação lê o último separador como decimal e transformava os 25.400 km contratados em 25,4 km.
// Um grupo de milhares nunca começa por zero, o que deixa em paz um 0.075 de custo por km.
const THOUSANDS_GROUPS = /^-?[1-9]\d{0,2}([.,]\d{3})+$/;

// Lê o número de uma célula. Quem grava tem de passar por aqui e não pelo parse genérico: senão a
// célula mostrava 25 400 km e a base de dados ficava com 25,4.
export const parseNumericText = (text: string) => {
    const core = text.replace(/\s/g, '').replace(/[^0-9.,-]/g, '');
    if (THOUSANDS_GROUPS.test(core)) return Number(core.replace(/[.,]/g, ''));
    return Utils.parseNumberAndUniformize(text);
};

// Um texto só é número se tiver mesmo algarismos: `Number('')` é 0, por isso sem esta guarda um
// "n/d" colado numa coluna de custo entrava como zero sem dar erro nenhum.
export const isNumericText = (raw: unknown) => {
    const text = String(raw ?? '').trim();
    if (!text || !/\d/.test(text)) return false;
    const parsed = parseNumericText(text);
    return parsed !== null && !isNaN(parsed);
};

// Tira o símbolo da moeda, os espaços e o resto do que vem agarrado ao número. O que não tiver um
// número lá dentro fica tal como veio: assim continua visível na célula e é a validação que o
// assinala, em vez de desaparecer.
export const sanitizeNumericText = (raw: unknown, kind: NumericKind) => {
    const text = String(raw ?? '').trim();
    if (!text) return '';
    if (!isNumericText(text)) return text;
    return formatNumericText(parseNumericText(text) as number, kind);
};

// Task #66347: as datas vindas do Excel trazem o separador da folha de origem (31.07.2026,
// 31-07-2026, 31 07 2026). Trocam-se por barras e mais nada — adivinhar a ordem dos campos daria
// dias e meses trocados. O que não ficar com cara de data fica tal como veio.
export const normalizeDateSeparators = (raw: unknown) => {
    const text = String(raw ?? '').trim();
    if (!text) return '';
    const normalized = text.replace(/[.\-\s]+/g, '/');
    return /^\d{1,4}\/\d{1,2}\/\d{1,4}$/.test(normalized) ? normalized : text;
};

// Normalização usada para casar texto colado do Excel com as opções de um dropdown (sem acentos,
// sem espaços a mais, sem maiúsculas) — ex: "Todos os Riscos" → ALL_RISKS.
export const normalizeForMatch = (value: string) =>
    value.normalize('NFD').replace(new RegExp('[\u0300-\u036f]', 'g'), '').trim().toUpperCase().replace(/\s+/g, ' ');

// Só letras e algarismos: é o que permite casar "06ZI06" com "06-ZI-06" ou "Ageas, Lda." com
// "Ageas Lda".
const squashForMatch = (value: string) => normalizeForMatch(value).replace(/[^A-Z0-9]/g, '');

// Devolve o valor da opção correspondente ao texto colado. Sem correspondência devolve o texto tal
// como veio: fica visível na célula e é o motor de validação que o assinala — melhor do que
// adivinhar por aproximação e gravar o registo errado.
export const matchOptionValue = (options: readonly SelectOption[], value: string) => {
    const normalized = normalizeForMatch(value);
    const exact = options.find(option => normalizeForMatch(option.value) === normalized)
        ?? options.find(option => normalizeForMatch(option.label) === normalized);
    if (exact) return exact.value;

    const squashed = squashForMatch(value);
    if (!squashed) return value.trim();

    const loose = options.find(option => squashForMatch(option.value) === squashed)
        ?? options.find(option => squashForMatch(option.label) === squashed);
    return loose?.value ?? value.trim();
};
